import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import Pagination from '../components/Pagination';
import { usePagination } from '../lib/usePagination';
import {
  Badge, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import DateRangePresets from '../components/DateRangePresets';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import { useDrilldown } from '../components/DrilldownProvider';
import { fetchTransfers } from '../api/transfers';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { TransfersPayload } from '../api/types';
import type { User } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

/**
 * v2.36 — Traspasos tab, full cycle. Shipments are the source store's stkxfr
 * tickets (valued at cost); receipts are the destination store's POs from
 * the counterpart-store supplier (transfer_sup_map). The recon table flags
 * parts where the two legs disagree inside the window. Transfer tickets are
 * not invoices, so the detail modal hides CFDI/PDF downloads.
 */
export default function InventoryTransfers() {
  useOutletContext<User>(); // layout provides the session; modal needs no role here
  const [from, setFrom] = useState(dateNDaysAgo(90));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [data, setData] = useState<TransfersPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Transfer tickets drill through the shared stack (JCD-59). invoice:false —
  // a transfer has no CFDI, so the download buttons stay hidden.
  const { open: openDrill } = useDrilldown();

  const load = () => {
    setErr(null);
    setData(null);
    fetchTransfers({ from, to })
      .then(setData)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [from, to]);

  const shipments = useMemo(() => data?.shipments ?? [], [data]);
  const receipts = useMemo(() => data?.receipts ?? [], [data]);
  const pgShip = usePagination(shipments);
  const pgRecv = usePagination(receipts);
  const discrepancies = useMemo(
    () => (data?.recon ?? []).filter((r) => r.shipped !== r.received),
    [data],
  );
  const reconConfigured = receipts.length > 0 || (data?.recon ?? []).length > 0;
  const showCost = shipments.some((r) => r.cost_mxn != null);

  const kpis = useMemo(() => {
    let units = 0, cost = 0, recvUnits = 0;
    const routes = new Map<string, number>();
    for (const r of shipments) {
      units += r.units;
      cost += r.cost_mxn ?? 0;
      const dest = r.dest_sid > 0 ? `T${r.dest_sid}` : (r.anum_name || String(r.anum));
      const key = `T${r.sid} → ${dest}`;
      routes.set(key, (routes.get(key) ?? 0) + 1);
    }
    for (const rc of receipts) recvUnits += rc.units;
    return { count: shipments.length, units, cost, recvUnits, routes };
  }, [shipments, receipts]);

  return (
    <div className="space-y-4">
      <div>
        <Title>{t.transfers.title}</Title>
        <Text className="text-tremor-content text-sm">{t.transfers.subtitle}</Text>
      </div>

      <Card className="space-y-3">
        <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
      </Card>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && data === null && <TableSkeleton rows={6} cols={8} />}

      {!err && data !== null && (
        <>
          <Card className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Kpi label={t.transfers.kpi.count} value={String(kpis.count)} />
            <Kpi label={t.transfers.kpi.units} value={String(kpis.units)} />
            {reconConfigured && <Kpi label={t.transfers.kpi.recvUnits} value={String(kpis.recvUnits)} />}
            {showCost && <Kpi label={t.transfers.kpi.cost} value={fmtMxn.format(kpis.cost)} />}
            <div>
              <div className="text-tremor-content text-xs">{t.transfers.route}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {[...kpis.routes].map(([route, n]) => (
                  <span key={route} className="text-xs px-2 py-0.5 rounded-tremor-full bg-tremor-background-subtle text-tremor-content-emphasis">
                    {route} · {n}
                  </span>
                ))}
              </div>
            </div>
          </Card>

          {/* Discrepancies front and center — that's the operational payoff. */}
          {reconConfigured ? (
            discrepancies.length > 0 ? (
              <Card>
                <Title className="text-base">{t.transfers.reconTitle}</Title>
                <Text className="text-xs text-tremor-content">{t.transfers.reconSubtitle}</Text>
                <Table className="mt-2">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>{t.transfers.route}</TableHeaderCell>
                      <TableHeaderCell>{t.transfers.cols.part}</TableHeaderCell>
                      <TableHeaderCell className="text-right">{t.transfers.cols.shipped}</TableHeaderCell>
                      <TableHeaderCell className="text-right">{t.transfers.cols.received}</TableHeaderCell>
                      <TableHeaderCell className="text-right">{t.transfers.cols.diff}</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {discrepancies.map((r) => (
                      <TableRow key={`${r.src_sid}-${r.dest_sid}-${r.line}-${r.part}`}>
                        <TableCell>T{r.src_sid} → T{r.dest_sid}</TableCell>
                        <TableCell>{r.line} {r.part}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.shipped}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.received}</TableCell>
                        <TableCell className={'text-right tabular-nums font-semibold ' +
                          (r.shipped > r.received
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-amber-700 dark:text-amber-400')}>
                          {r.shipped - r.received > 0 ? '+' : ''}{r.shipped - r.received}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            ) : shipments.length > 0 ? (
              <Text className="text-emerald-700 dark:text-emerald-400">{t.transfers.reconClear}</Text>
            ) : null
          ) : (
            shipments.length > 0 && (
              <Text className="text-xs text-tremor-content">{t.transfers.reconUnavailable}</Text>
            )
          )}

          {shipments.length === 0 ? (
            <EmptyState message={t.transfers.empty} />
          ) : (
            <Card className="p-0 overflow-hidden">
              <div className="px-4 pt-3">
                <Title className="text-base">{t.transfers.shipmentsTitle}</Title>
              </div>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.transfers.cols.date}</TableHeaderCell>
                    <TableHeaderCell>{t.transfers.cols.tnum}</TableHeaderCell>
                    <TableHeaderCell>{t.transfers.cols.from}</TableHeaderCell>
                    <TableHeaderCell>{t.transfers.cols.to}</TableHeaderCell>
                    <TableHeaderCell>{t.transfers.cols.slid}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.transfers.cols.lines}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.transfers.cols.units}</TableHeaderCell>
                    {showCost && <TableHeaderCell className="text-right">{t.transfers.cols.cost}</TableHeaderCell>}
                    <TableHeaderCell>{t.transfers.cols.status}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pgShip.pageRows.map((r) => (
                    <TableRow
                      key={`${r.sid}-${r.tnum}`}
                      className="cursor-pointer hover:bg-tremor-background-muted"
                      onClick={() => openDrill({ kind: 'tkt', sid: r.sid, tnum: r.tnum, invoice: false })}
                    >
                      <TableCell>{r.idate}</TableCell>
                      <TableCell>{r.tnum}</TableCell>
                      <TableCell>T{r.sid}</TableCell>
                      <TableCell>{r.dest_sid > 0 ? `T${r.dest_sid}` : ''} {r.anum_name || r.anum}</TableCell>
                      <TableCell>{r.slid || '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.lines}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.units}</TableCell>
                      {showCost && (
                        <TableCell className="text-right tabular-nums">
                          {r.cost_mxn != null ? fmtMxn.format(r.cost_mxn) : '—'}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge color={r.completed && r.invoiced ? 'emerald' : 'amber'}>
                          {r.completed && r.invoiced ? t.transfers.statusDone : t.transfers.statusOpen}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination {...pgShip} className="px-4" />
            </Card>
          )}

          {receipts.length > 0 && (
            <Card className="p-0 overflow-hidden">
              <div className="px-4 pt-3">
                <Title className="text-base">{t.transfers.receiptsTitle}</Title>
              </div>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{t.transfers.cols.date}</TableHeaderCell>
                    <TableHeaderCell>{t.transfers.cols.onum}</TableHeaderCell>
                    <TableHeaderCell>{t.transfers.route}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.transfers.cols.lines}</TableHeaderCell>
                    <TableHeaderCell className="text-right">{t.transfers.cols.units}</TableHeaderCell>
                    {receipts.some((rc) => rc.cost_mxn != null) && (
                      <TableHeaderCell className="text-right">{t.transfers.cols.cost}</TableHeaderCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pgRecv.pageRows.map((rc) => (
                    <TableRow key={`${rc.sid_dest}-${rc.onum}-${rc.receive_no}`}>
                      <TableCell>{rc.date}</TableCell>
                      <TableCell>{rc.onum} · #{rc.receive_no}</TableCell>
                      <TableCell>T{rc.sid_src} → T{rc.sid_dest}</TableCell>
                      <TableCell className="text-right tabular-nums">{rc.lines}</TableCell>
                      <TableCell className="text-right tabular-nums">{rc.units}</TableCell>
                      {receipts.some((x) => x.cost_mxn != null) && (
                        <TableCell className="text-right tabular-nums">
                          {rc.cost_mxn != null ? fmtMxn.format(rc.cost_mxn) : '—'}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination {...pgRecv} className="px-4" />
            </Card>
          )}
        </>
      )}

    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
